import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import makeStyles from '@mui/styles/makeStyles';
import KeyIcon from '@mui/icons-material/VpnKey';
import { Typography, Popper, Grow, MenuList, Paper, ClickAwayListener, Avatar, Grid, ListItemIcon, Box, LinearProgress, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ExitToApp } from '@mui/icons-material/';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CheckIcon from '@mui/icons-material/Check';
import ContrastIcon from '@mui/icons-material/Contrast';
import User from '../../model/User';
import { getExperienceNeedleByLevel, getFirstLettersToAvatar } from '../../utils';
import UserPhoto from '../UserPhoto';
import PhotoIcon from '@mui/icons-material/PhotoCamera';
import { changeViewModeAction, useViewMode } from '../../reducer/viewMode';
import { changeThemeAction, useCbxTheme } from '../../reducer/theme';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useDispatch } from 'react-redux';
import UserApi from '../../service/UserApi';
import SavingsIcon from '@mui/icons-material/Savings';
import { useHistory } from 'react-router-dom';
import { keyframes } from '@mui/system';
import { useLanzacoinShop } from '../../context/LanzacoinShopContext';

interface ProfileMenuProps {
    user: User;
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => any;
    onLogOut: () => any;
    openChangePassword: () => any;
    openChangePhoto: () => any;
}

const useStyles = makeStyles((theme) => ({
    menuContent: {
        minWidth: '200px',
        padding: theme.spacing(2),
    },
    avatar: {
        margin: theme.spacing(1),
        fontSize: 22
    }
}));

const glowAnimation = keyframes`
    0% {
        box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2);
    }
    50% {
        box-shadow: 0 0 8px 3px rgba(255, 215, 0, 0.7), 0 0 16px 6px rgba(255, 180, 0, 0.35);
    }
    100% {
        box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2);
    }
`;

function ProfileMenu(props: ProfileMenuProps) {
    const { user, isOpen, anchorEl, onClose, onLogOut, openChangePassword, openChangePhoto } = props;
    const viewMode = useViewMode();
    const cbxTheme = useCbxTheme();
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { hasProfileHighlight } = useLanzacoinShop();

    const [currentUser, setCurrentUser] = useState<User|null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && user.id) {
            const userApi = new UserApi(false);
            setIsLoading(true);
            userApi.getById(user.id)
                .then((updatedUser) => {
                    setCurrentUser(updatedUser);
                })
                .catch((error) => {
                    console.error('Erro ao buscar dados do usuário:', error);
                    setCurrentUser(user);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [isOpen, user.id]);

    const totalExperience = Math.max(0, currentUser?.experience ?? 0);
    const formattedTotalExperience = React.useMemo(() => new Intl.NumberFormat('pt-BR').format(totalExperience), [totalExperience]);
    const formattedLanzaCoin = React.useMemo(() => new Intl.NumberFormat('pt-BR').format(currentUser?.lanza_coin ?? 0), [currentUser?.lanza_coin]);
    const experienceInfo = React.useMemo(() => {
        const experienceMap = getExperienceNeedleByLevel();
        let experience = totalExperience;
        let currentLevel = 1;

        for (let level = 1; level <= 50; level++) {
            const requirement = experienceMap[level];

            if (requirement === null) {
                return {
                    level: 50,
                    xpIntoLevel: 0,
                    xpToNext: null,
                    progress: 100
                };
            }

            if (experience >= requirement) {
                experience -= requirement;
                currentLevel = level + 1;
                const nextRequirement = experienceMap[level + 1];

                if (nextRequirement === null) {
                    return {
                        level: currentLevel,
                        xpIntoLevel: 0,
                        xpToNext: null,
                        progress: 100
                    };
                }
            } else {
                const progress = requirement > 0 ? Math.min(100, (experience / requirement) * 100) : 0;
                return {
                    level,
                    xpIntoLevel: experience,
                    xpToNext: requirement,
                    progress
                };
            }
        }

        return {
            level: 50,
            xpIntoLevel: 0,
            xpToNext: null,
            progress: 100
        };
    }, [totalExperience]);

    if (!currentUser) {
        return null;
    }

    return (
        <Popper open={isOpen} anchorEl={anchorEl} transition style={{ zIndex: 9999 }}>
            {({ TransitionProps, placement }) => (
                <ClickAwayListener onClickAway={onClose}>
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <Paper className={classes.menuContent}>
                            <>
                                <Grid container justifyContent="center" alignItems="center">
                                    <Avatar
                                        className={classes.avatar}
                                        sx={hasProfileHighlight ? {
                                            border: '2px solid #FFD700',
                                            animation: `${glowAnimation} 2s ease-in-out infinite`,
                                            borderRadius: '50%'
                                        } : undefined}
                                    >{currentUser.photo ? <UserPhoto id={currentUser.id} photo={currentUser.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getFirstLettersToAvatar(currentUser.name)}</Avatar>
                                </Grid>
                                <Grid container justifyContent="center" alignItems="center">
                                    <Typography align='center'>{currentUser.name}</Typography>
                                </Grid>
                                {currentUser.use_lanza_coin && <Box mt={2} mb={1} textAlign="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                        <Chip
                                            icon={<MonetizationOnIcon sx={{ color: '#FFD700 !important' }} />}
                                            label={isLoading ? '...' : formattedLanzaCoin}
                                            sx={{
                                                backgroundColor: '#FFF8DC',
                                                color: '#B8860B',
                                                fontWeight: 600,
                                                border: '1px solid #FFD700',
                                                '& .MuiChip-icon': {
                                                    color: '#FFD700'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {isLoading ? 'Carregando...' : `Nível ${experienceInfo.level}`}
                                        </Typography>
                                        {!isLoading && <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                                            ({`${formattedTotalExperience} XP`})
                                        </Typography>}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        {experienceInfo.xpToNext !== null ? `${experienceInfo.xpIntoLevel} / ${experienceInfo.xpToNext} XP` : 'Nível máximo atingido'}
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={experienceInfo.progress}
                                        sx={{
                                            mt: 1,
                                            height: 8,
                                            borderRadius: 5,
                                            backgroundColor: (theme) => theme.palette.background.default === '#202326'
                                                ? theme.palette.action.selected
                                                : 'grey.200',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 5,
                                            }
                                        }}
                                    />
                                </Box>}

                                <MenuList>
                                    <Box sx={{ px: 1, py: 0.5 }}>
                                        <ToggleButtonGroup
                                            exclusive
                                            fullWidth
                                            size="small"
                                            value={cbxTheme}
                                            aria-label="Selecionar tema"
                                            onChange={(_, theme) => theme && dispatch(changeThemeAction(theme))}
                                            sx={{
                                                width: '100%',
                                                '& .MuiToggleButton-root': {
                                                    flex: 1,
                                                    minWidth: 0,
                                                    px: 1,
                                                    textTransform: 'none'
                                                }
                                            }}
                                        >
                                            <ToggleButton value="light" aria-label="Tema light">light</ToggleButton>
                                            <ToggleButton value="dark" aria-label="Tema dark">dark</ToggleButton>
                                            <ToggleButton value="dark2" aria-label="Tema dark2">dark2</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    {currentUser.use_lanza_coin && <MenuItem onClick={() => history.push('/system/lanzacoins')}>
                                        <ListItemIcon style={{ minWidth: 25 }}>
                                            <SavingsIcon />
                                        </ListItemIcon>
                                        LanzaCoins
                                    </MenuItem>}
                                    {user.perm_task && <MenuItem onClick={() => dispatch(changeViewModeAction(viewMode === 'mobile' ? 'desktop' : 'mobile'))}>
                                        <ListItemIcon style={{ minWidth: 25 }}>
                                            {viewMode === 'mobile' ? <PhoneAndroidIcon /> : <DesktopWindowsIcon />}
                                        </ListItemIcon>
                                        Alterar para modo {viewMode === 'mobile' ? 'Desktop' : 'Mobile'}
                                    </MenuItem>}
                                    <MenuItem onClick={openChangePhoto}>
                                        <ListItemIcon style={{ minWidth: 25 }}>
                                            <PhotoIcon />
                                        </ListItemIcon>
                                        Alterar Foto
                                    </MenuItem>
                                    <MenuItem onClick={openChangePassword}>
                                        <ListItemIcon style={{ minWidth: 25 }}>
                                            <KeyIcon />
                                        </ListItemIcon>
                                        Alterar Senha
                                    </MenuItem>
                                    <MenuItem onClick={onLogOut}>
                                        <ListItemIcon style={{ minWidth: 25 }}>
                                            <ExitToApp />
                                        </ListItemIcon>
                                        Sair
                                    </MenuItem>
                                </MenuList>
                            </>
                        </Paper>
                    </Grow>
                </ClickAwayListener>
            )}
        </Popper>
    )
}

export default ProfileMenu;